import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FOOTER_CONTENT } from './footerData'; 
import EvalHeader from './EvalHeader';
import { ChevronRight, ArrowLeft, Mail, ShieldCheck } from 'lucide-react';

const FooterPage = () => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const pageData = FOOTER_CONTENT[pageId];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!pageData) navigate('/');
  }, [pageId, pageData, navigate]);

  if (!pageData) return null;

  return (
    <div className="bg-white min-h-screen font-sans selection:bg-indigo-100 selection:text-indigo-900">
      <EvalHeader />

      {/* READING PROGRESS BAR */}
      <div className="fixed top-0 left-0 w-full h-1 z-[110]">
        <div className="h-full bg-indigo-600 animate-progress origin-left" />
      </div>

      <main className="max-w-7xl mx-auto pt-32 pb-24 px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row gap-16">
          
          {/* --- LEFT SIDEBAR: STICKY NAVIGATION --- */}
          <aside className="lg:w-64 shrink-0 lg:sticky lg:top-32 h-fit space-y-10">
            <div>
              <button 
                onClick={() => navigate('/')}
                className="group flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-indigo-600 transition-all mb-10"
              >
                <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
                Back to Engine
              </button>
              
              <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-900 mb-6 px-4">
                Documentation
              </h4>
              <nav className="space-y-1">
                {Object.keys(FOOTER_CONTENT).map((key) => (
                  <Link
                    key={key}
                    to={`/${key}`}
                    className={`flex items-center justify-between px-4 py-3 rounded-xl text-[11px] font-bold uppercase tracking-widest transition-all ${
                      pageId === key 
                        ? 'bg-indigo-50 text-indigo-600 border border-indigo-100 shadow-sm' 
                        : 'text-slate-400 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    {FOOTER_CONTENT[key].title.split(' ')[0]}
                    {pageId === key && <ChevronRight size={14} />}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Quick Status Info */}
            <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100 hidden lg:block">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck size={16} className="text-emerald-500" />
                <span className="text-[10px] font-black uppercase text-slate-900">Verified</span>
              </div>
              <p className="text-[10px] leading-relaxed text-slate-500 font-medium uppercase">
                Content current as of Build V1.0.2 Stable. Last updated April 2026.
              </p>
            </div>
          </aside>

          {/* --- RIGHT CONTENT AREA --- */}
          <section className="flex-1 max-w-3xl">
            {/* Header section */}
            <header className="mb-16">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-slate-900 text-white text-[9px] font-black uppercase tracking-[0.2em] mb-6">
                Official Document // {pageId}
              </div>
              <h1 className="text-5xl md:text-7xl font-black text-slate-900 uppercase tracking-tighter leading-[0.85] mb-8">
                {pageData.title}
              </h1>
              <div className="h-2 w-24 bg-indigo-600" />
            </header>

            {/* Injected Content from footerData.js */}
            <article className="prose prose-slate prose-headings:uppercase prose-headings:tracking-tighter prose-headings:font-black animate-in fade-in slide-in-from-bottom-8 duration-1000">
              {pageData.content}
            </article>

            {/* FOOTER OF THE PAGE */}
            <div className="mt-24 pt-12 border-t border-slate-100">
              <div className="bg-slate-900 rounded-[2.5rem] p-10 flex flex-col md:flex-row items-center justify-between gap-8 overflow-hidden relative">
                {/* Decorative Pattern */}
                <div className="absolute inset-0 opacity-10 pointer-events-none overflow-hidden">
                   <div className="absolute -right-10 -bottom-10 text-9xl font-black text-white italic">NDT</div>
                </div>

                <div className="relative z-10">
                  <h3 className="text-white text-xl font-black uppercase tracking-tight mb-2">Further Clarity Needed?</h3>
                  <p className="text-slate-400 text-sm font-medium">Connect with our NDT technical leads for policy inquiries.</p>
                </div>
                
                <a 
                  href="mailto:support@evalndt.com"
                  className="relative z-10 px-8 py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-2xl font-black uppercase text-[11px] tracking-widest transition-all flex items-center gap-3 shadow-xl shadow-indigo-900/40"
                >
                  <Mail size={16} /> Contact Support
                </a>
              </div>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
};

export default FooterPage;